import type { ExchangeStatus, ExchangeSummary } from '../types/api';

// Status-transition eligibility for exchange runtime controls. Client-side mirror of the
// backend's exchange lifecycle rules (see docs/ux audit — no server-driven "available
// actions" list exists yet). Shared between ExchangeDetailPage (single-exchange actions) and
// ExchangesPage (bulk actions), both of which only need the exchange's `status` to decide.

export function canRetryExchange(exchange: { status: ExchangeStatus }): boolean {
  return exchange.status === 'FAILED';
}

export function canDeadLetterExchange(exchange: { status: ExchangeStatus }): boolean {
  return exchange.status === 'FAILED' || exchange.status === 'WAITING_RETRY';
}

export function canReplayExchange(exchange: { status: ExchangeStatus }): boolean {
  return exchange.status === 'FAILED' || exchange.status === 'DEAD_LETTERED';
}

// Mirrors the backend's ARCHIVED transitions in exchange-lifecycle.ts: reachable from every
// unresolved status, so an operator can remove any recovery-queue row (not just dead-lettered
// ones) once they've decided no further retry/replay is coming.
export function canArchiveExchange(exchange: { status: ExchangeStatus }): boolean {
  return exchange.status === 'FAILED' || exchange.status === 'WAITING_RETRY' || exchange.status === 'DEAD_LETTERED';
}

export interface ExchangePartnerGroup {
  key: string;
  label: string;
  exchanges: ExchangeSummary[];
}

// Clusters exchanges by partner for DLQ-style triage on ExchangesPage. Only partner grouping
// is offered here (not failure reason, unlike the AS2 DLQ page) because ExchangeSummary — the
// shape returned by the list endpoint — carries no error/reason field; that only exists on
// the per-exchange detail record, and fetching detail for every visible row just to group by
// reason would mean N+1 requests. See the cross-link/clustering gap note for detail.
export function groupExchangesByPartner(exchanges: ExchangeSummary[]): ExchangePartnerGroup[] {
  const groups = new Map<string, ExchangePartnerGroup>();
  for (const exchange of exchanges) {
    const key = exchange.partnerId ?? 'Unknown partner';
    const existing = groups.get(key);
    if (existing) {
      existing.exchanges.push(exchange);
    } else {
      groups.set(key, { key, label: key, exchanges: [exchange] });
    }
  }
  return Array.from(groups.values()).sort((a, b) => b.exchanges.length - a.exchanges.length);
}
